import React from 'react';
import cx from 'classnames';
import {Button, Icon, LineClamp, Message, Picture} from 'epic-diesel-common';
import {sizes, svgs} from 'epic-diesel-common/src/constants';
import {getMessage} from 'epic-diesel-common/src/components/message/Message';
import {isMobileBreakpoint} from 'epic-diesel-common/src/utils/BreakpointUtils';
import breakpoints, {BreakpointName} from 'epic-diesel-common/src/constants/breakpoints';
import {PostPurchaseDataMap} from 'epic-diesel-common/src/redux/actions/toast.actions';
import {ImageData} from 'epic-diesel-common/src/cmsTypes';

import styles from './PostPurchaseToastItem.pcss';

type ToastAction = (toastId: number) => void;

interface Props {
    breakpoint: BreakpointName;
    className?: string;
    data: PostPurchaseDataMap;
    dismissToastItem: ToastAction;
    id: number;
    image: ImageData;
    messages: Record<string, string>;
    productName: string;
    secondaryAction?: ToastAction;
    useSecondaryCTA: boolean;
}

const PostPurchaseToastItem: React.FunctionComponent<Props> = ({
    breakpoint,
    className,
    dismissToastItem,
    image,
    messages,
    id,
    productName,
    secondaryAction,
    useSecondaryCTA
}: Props) => {
    const titleCode = 'epic.diesel.toast.installation.title';
    const descriptionCode = 'epic.diesel.toast.installation.description';
    const description: string = getMessage(messages, descriptionCode, [productName]);
    const primaryCTACode = 'epic.diesel.toast.installation.see_in_library';
    const secondaryCTACode = 'epic.diesel.toast.installation.install_now';

    const maxLines: number = isMobileBreakpoint(breakpoint) ? 2 : 3;

    return (
        <div className={cx(className, styles.alert)}>
            <div className={styles.image}>
                <Picture
                    srcSet={{
                        [breakpoints.name.XSMALL]: image.src
                    }}
                    className={styles.inner}
                />
            </div>
            <div className={styles.content}>
                <div className={styles.text}>
                    <Message className={styles.title} code={titleCode} />
                    <div className={styles.description}>
                        <LineClamp lines={maxLines} overflowNode="...">
                            {description}
                        </LineClamp>
                    </div>
                </div>
                <div className={styles.ctas}>
                    {useSecondaryCTA && (
                        <Button
                            className={styles.secondaryCta}
                            onClick={() => {
                                secondaryAction(id);
                                dismissToastItem(id);
                            }}
                            hasHover={false}
                            hasMinWidth={false}>
                            <Message className={styles.ctaText} code={secondaryCTACode} />
                        </Button>
                    )}
                    <Button
                        className={styles.primaryCta}
                        onClick={() => dismissToastItem(id)}
                        to={'/library'}
                        hasHover={false}
                        hasMinWidth={false}>
                        <Message className={styles.ctaText} code={primaryCTACode} />
                    </Button>
                </div>
                <button className={styles.closeButton} onClick={() => dismissToastItem(id)}>
                    <Icon icon={svgs.CLOSE} className={styles.closeIcon} size={sizes.CUSTOM} />
                </button>
            </div>
        </div>
    );
};

export default PostPurchaseToastItem;
